import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Card, CardBody, Col, Container, Row,
  Badge, Button, Input, Spinner, Modal,
  ModalHeader, ModalBody, ModalFooter, Alert, Table,
} from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";

const BASE_URL = "https://hiflowapi.stechomeyazilim.info";

// PAYMENT_LOGS.Status değerleri (PaycellController ile aynı)
const ST_SUCCESS  = "Success";
const ST_REVERSED = "Reversed";
const ST_REFUNDED = "Refunded";

const fmtAmount = (n) =>
  (Number(n) || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return "-";
  try { return new Date(d).toLocaleString("tr-TR"); } catch { return d; }
};

const Odemeler = () => {
  document.title = "Ödemeler | LojGo";

  const [orders,   setOrders]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [alert,    setAlert]    = useState(null); // { type, msg }
  const [actionRef, setActionRef] = useState(null); // işlemi süren OrderNumber

  const [cancelRow, setCancelRow] = useState(null); // iptal onay modalı
  const [refundRow, setRefundRow] = useState(null); // iade modalı
  const [refundAmount, setRefundAmount] = useState("");

  const showAlert = (type, msg) => {
    setAlert({ type, msg });
    setTimeout(() => setAlert(null), 6000);
  };

  // ─── VERİ ÇEKME ───────────────────────────────────────────────
  // PAYMENT_LOGS ham log; bir ödeme birden çok satır (Session/3D/Success/Inquiry/Reversed/Refunded).
  // OrderNumber'a göre gruplayıp Success olanları tek satır olarak gösteriyoruz.
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${BASE_URL}/PAYMENT_LOGS?$orderby=ID desc&$top=2000&$expand=Users`
      );
      const data = await res.json();
      const logs = data.value || [];

      const byOrder = {};
      for (const l of logs) {
        const key = l.OrderNumber;
        if (!key) continue;
        if (!byOrder[key]) byOrder[key] = { ref: key, statuses: [], successLog: null, refundedSum: 0 };
        const g = byOrder[key];
        g.statuses.push(l.Status);
        if (l.Status === ST_SUCCESS && !g.successLog) g.successLog = l;
        if (l.Status === ST_REFUNDED) g.refundedSum += Number(l.TransactionAmount) || 0;
      }

      const rows = Object.values(byOrder)
        .filter(g => g.successLog) // sadece tamamlanmış ödemeler
        .map(g => {
          const s = g.successLog;
          const reversed = g.statuses.includes(ST_REVERSED);
          const refunded = g.statuses.includes(ST_REFUNDED);
          return {
            ref:      g.ref,
            id:       s.ID,
            userId:   s.Users_ID,
            userName: s.Users?.Users_NameSurname || `#${s.Users_ID ?? "-"}`,
            amount:   Number(s.TransactionAmount) || 0,
            date:     s.CreatedAt,
            ip:       s.IpAddress,
            reversed,
            refunded,
            refundedSum: g.refundedSum,
          };
        })
        .sort((a, b) => b.id - a.id);

      setOrders(rows);
    } catch (e) {
      showAlert("danger", `Ödemeler yüklenemedi: ${e.message}`);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter(r =>
      (r.userName || "").toLowerCase().includes(q) ||
      (r.ref || "").toLowerCase().includes(q)
    );
  }, [orders, search]);

  const totals = useMemo(() => {
    let collected = 0, refunded = 0;
    orders.forEach(r => {
      if (!r.reversed) collected += r.amount;
      if (r.reversed) refunded += r.amount;
      refunded += r.refundedSum;
    });
    return { collected, refunded, count: orders.length };
  }, [orders]);

  const statusBadge = (r) => {
    if (r.reversed) return <Badge color="secondary">İptal Edildi</Badge>;
    if (r.refunded) return <Badge color="warning">İade Edildi{r.refundedSum ? ` (${fmtAmount(r.refundedSum)} ₺)` : ""}</Badge>;
    return <Badge color="success">Başarılı</Badge>;
  };

  // ─── İPTAL (reverseAll) ───────────────────────────────────────
  const doReverse = async (row) => {
    setActionRef(row.ref);
    try {
      const res = await fetch(`${BASE_URL}/api/Paycell/Reverse`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ ReferenceNumber: row.ref }),
      });
      const raw = await res.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch { /* JSON değil */ }

      const ok = data.status === "success" || data.Status === "success" ||
                 data.success === true || data.Success === true;
      if (ok) {
        showAlert("success", `${row.ref} numaralı ödeme iptal edildi.`);
        await loadData();
      } else {
        const reason =
          data.responseDescription || data.ResponseDescription ||
          data.message || data.Message || data.error || data.Error ||
          (raw && raw.slice(0, 200)) ||
          `HTTP ${res.status} ${res.statusText || ""}`.trim();
        console.error("Reverse başarısız:", res.status, raw);
        showAlert("danger", `İptal başarısız: ${reason}`);
      }
    } catch (e) {
      showAlert("danger", `İptal hatası: ${e.message}`);
    } finally {
      setActionRef(null);
      setCancelRow(null);
    }
  };

  // ─── İADE (refundAll) ─────────────────────────────────────────
  const doRefund = async (row, amount) => {
    setActionRef(row.ref);
    try {
      const body = { ReferenceNumber: row.ref };
      const amt = parseFloat(amount);
      if (!isNaN(amt) && amt > 0) body.Amount = amt; // boşsa tam iade
      const res = await fetch(`${BASE_URL}/api/Paycell/Refund`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (data.status === "success") {
        showAlert("success", `${row.ref} için ${fmtAmount(data.refundedAmount ?? amt ?? row.amount)} ₺ iade edildi.`);
        await loadData();
      } else {
        showAlert("danger", `İade başarısız: ${data.responseDescription || data.Message || "Bilinmeyen hata"}`);
      }
    } catch (e) {
      showAlert("danger", `İade hatası: ${e.message}`);
    } finally {
      setActionRef(null);
      setRefundRow(null);
      setRefundAmount("");
    }
  };

  const openRefund = (row) => {
    setRefundRow(row);
    setRefundAmount(row.amount.toFixed(2));
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Ödemeler" breadcrumbItem="Paycell Ödemeleri" />

          {alert && <Alert color={alert.type}>{alert.msg}</Alert>}

          <Row>
            <Col md={4}>
              <Card><CardBody>
                <p className="text-muted mb-1">Toplam Tahsilat</p>
                <h4 className="mb-0 text-success">{fmtAmount(totals.collected)} ₺</h4>
              </CardBody></Card>
            </Col>
            <Col md={4}>
              <Card><CardBody>
                <p className="text-muted mb-1">İptal / İade</p>
                <h4 className="mb-0 text-warning">{fmtAmount(totals.refunded)} ₺</h4>
              </CardBody></Card>
            </Col>
            <Col md={4}>
              <Card><CardBody>
                <p className="text-muted mb-1">İşlem Sayısı</p>
                <h4 className="mb-0">{totals.count}</h4>
              </CardBody></Card>
            </Col>
          </Row>

          <Card>
            <CardBody>
              <Row className="mb-3">
                <Col md={6}>
                  <Input
                    type="text"
                    placeholder="Kullanıcı adı veya sipariş no ara..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </Col>
                <Col md={6} className="text-md-end mt-2 mt-md-0">
                  <Button color="light" onClick={loadData} disabled={loading}>
                    {loading ? <Spinner size="sm" /> : "Yenile"}
                  </Button>
                </Col>
              </Row>

              {loading ? (
                <div className="text-center py-5"><Spinner /></div>
              ) : filtered.length === 0 ? (
                <div className="text-center text-muted py-5">Kayıt bulunamadı.</div>
              ) : (
                <div className="table-responsive">
                  <Table className="table-nowrap align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Tarih</th>
                        <th>Kullanıcı</th>
                        <th>Sipariş No</th>
                        <th className="text-end">Tutar</th>
                        <th>Durum</th>
                        <th className="text-end">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map(r => {
                        const busy = actionRef === r.ref;
                        const closed = r.reversed || r.refunded;
                        return (
                          <tr key={r.ref}>
                            <td>{fmtDate(r.date)}</td>
                            <td>{r.userName}</td>
                            <td><small className="text-muted">{r.ref}</small></td>
                            <td className="text-end fw-semibold">{fmtAmount(r.amount)} ₺</td>
                            <td>{statusBadge(r)}</td>
                            <td className="text-end">
                              {closed ? (
                                <span className="text-muted">—</span>
                              ) : busy ? (
                                <Spinner size="sm" />
                              ) : (
                                <>
                                  <Button
                                    color="danger" size="sm" outline className="me-2"
                                    onClick={() => setCancelRow(r)}
                                  >
                                    İptal
                                  </Button>
                                  <Button
                                    color="warning" size="sm" outline
                                    onClick={() => openRefund(r)}
                                  >
                                    İade
                                  </Button>
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* ── İPTAL onay modalı ─────────────────────────────── */}
      <Modal isOpen={!!cancelRow} toggle={() => setCancelRow(null)} centered>
        <ModalHeader toggle={() => setCancelRow(null)}>Ödemeyi İptal Et</ModalHeader>
        <ModalBody>
          {cancelRow && (
            <p className="mb-0">
              <strong>{cancelRow.userName}</strong> kullanıcısının{" "}
              <strong>{fmtAmount(cancelRow.amount)} ₺</strong> tutarındaki ödemesi
              tamamen iptal edilecek (aynı gün / mutabakat öncesi). Onaylıyor musunuz?
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setCancelRow(null)}>Vazgeç</Button>
          <Button color="danger" onClick={() => doReverse(cancelRow)} disabled={actionRef === cancelRow?.ref}>
            {actionRef === cancelRow?.ref ? <Spinner size="sm" /> : "İptal Et"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── İADE modalı (tam/kısmi) ───────────────────────── */}
      <Modal isOpen={!!refundRow} toggle={() => setRefundRow(null)} centered>
        <ModalHeader toggle={() => setRefundRow(null)}>İade Yap</ModalHeader>
        <ModalBody>
          {refundRow && (
            <>
              <p>
                <strong>{refundRow.userName}</strong> — ödeme tutarı{" "}
                <strong>{fmtAmount(refundRow.amount)} ₺</strong>. İade edilecek tutarı girin
                (tam iade için olduğu gibi bırakın). Mutabakat sonrası kullanılır.
              </p>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                max={refundRow.amount}
                value={refundAmount}
                onChange={e => setRefundAmount(e.target.value)}
              />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setRefundRow(null)}>Vazgeç</Button>
          <Button color="warning" onClick={() => doRefund(refundRow, refundAmount)} disabled={actionRef === refundRow?.ref}>
            {actionRef === refundRow?.ref ? <Spinner size="sm" /> : "İade Et"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Odemeler;
